"use client";
import { cn } from "../../lib/utilsComprehensive";
import React, {
  forwardRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { OptimizedGlass } from "../../primitives";
import {
  usePredictiveEngine,
  useInteractionRecorder,
} from "../advanced/GlassPredictiveEngine";
import { useAchievements } from "../advanced/GlassAchievementSystem";
import { useBiometricAdaptation } from "../advanced/GlassBiometricAdaptation";
import { useEyeTracking } from "../advanced/GlassEyeTracking";
import { useSpatialAudio } from "../advanced/GlassSpatialAudio";
import type { ConsciousnessFeatures } from "../layout/GlassContainer";
import { ContrastGuard } from "@/components/accessibility/ContrastGuard";
import { ANIMATION } from "../../tokens/designConstants";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { createGlassStyle } from "../../core/mixins/glassMixins";

const TABLE_TEXT =
  "var(--glass-theme-text, var(--glass-text-primary, rgba(248,250,252,0.96)))";
const TABLE_SECONDARY_TEXT =
  "var(--glass-theme-text-secondary, var(--glass-text-secondary, rgba(226,232,240,0.76)))";
const tableTextStyle: React.CSSProperties = { color: TABLE_TEXT };
const tableSecondaryTextStyle: React.CSSProperties = {
  color: TABLE_SECONDARY_TEXT,
};
const tableControlStyle: React.CSSProperties = {
  minHeight: 40,
  border: "1px solid rgba(255, 255, 255, 0.16)",
  borderRadius: 12,
  color: TABLE_TEXT,
  background:
    "linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.018))",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.16), 0 8px 24px rgba(3,7,18,0.12)",
};
const tableNativeControlStyle: React.CSSProperties = {
  ...tableControlStyle,
  appearance: "none",
  WebkitAppearance: "none",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
};

export type GlassDataTableRow = Record<string, unknown>;
export type GlassDataTableCellValue = unknown;

export interface ColumnDef<T extends GlassDataTableRow = GlassDataTableRow> {
  id?: string;
  header: string | ((props: { column: ColumnDef<T> }) => React.ReactNode);
  accessorKey?: string;
  accessorFn?: (row: T) => GlassDataTableCellValue;
  cell?: (props: { row: T; value: GlassDataTableCellValue }) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  width?: string | number;
  align?: "left" | "center" | "right";
  enableSorting?: boolean;
  enableHiding?: boolean;
}

export interface SortState {
  id: string;
  desc: boolean;
}

export type FilterState = Record<string, GlassDataTableCellValue>;

export interface GlassDataTableProps<
  T extends GlassDataTableRow = GlassDataTableRow,
> extends ConsciousnessFeatures {
  /**
   * Table data
   */
  data?: T[];
  /**
   * Column definitions
   */
  columns: ColumnDef<T>[];
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Empty state message
   */
  emptyMessage?: string;
  /**
   * Table variant
   */
  variant?: "default" | "striped" | "bordered" | "minimal";
  /**
   * Table size
   */
  size?: "sm" | "md" | "lg";
  /**
   * Enable sorting
   */
  sortable?: boolean;
  /**
   * Enable filtering
   */
  filterable?: boolean;
  /**
   * Enable search
   */
  searchable?: boolean;
  /**
   * Search placeholder
   */
  searchPlaceholder?: string;
  /**
   * Enable pagination
   */
  pagination?: boolean;
  /**
   * Page size options
   */
  pageSizeOptions?: number[];
  /**
   * Initial page size
   */
  initialPageSize?: number;
  /** Compact mode for constrained cards, drawers, and documentation previews. */
  compact?: boolean;
  /** Contain the table in a bounded vertical viewport. */
  contained?: boolean;
  /** Maximum rendered height when contained or compact. */
  maxHeight?: number | string;
  /** Maximum rendered width when contained or compact. */
  maxWidth?: number | string;
  /** Hide the search/actions header in compact mode if desired. */
  showHeader?: boolean;
  /** Hide pagination controls in compact mode if desired. */
  showFooter?: boolean;
  /**
   * Enable row selection
   */
  selectable?: boolean;
  /**
   * Selection mode
   */
  selectionMode?: "single" | "multiple";
  /**
   * Selected rows
   */
  selectedRows?: string[];
  /**
   * Selection change handler
   */
  onSelectionChange?: (selectedRows: string[]) => void;
  /**
   * Row click handler
   */
  onRowClick?: (row: T) => void;
  /**
   * Get row ID
   */
  getRowId?: (row: T, index?: number) => string;
  /**
   * Custom row props
   */
  getRowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement>;
  /**
   * Sticky header
   */
  stickyHeader?: boolean;
  /**
   * Table actions
   */
  actions?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  /**
   * Optional per-column cell renderers by column id or accessorKey
   */
  cellRenderers?: Record<
    string,
    (value: GlassDataTableCellValue, row: T) => React.ReactNode
  >;
  /**
   * Optional rich empty state
   */
  emptyState?: {
    icon?: React.ReactNode;
    message?: string;
    description?: string;
  };
}

const getColumnValue = <T extends GlassDataTableRow>(
  row: T,
  column: ColumnDef<T>
): GlassDataTableCellValue => {
  if (column.accessorFn) {
    return column.accessorFn(row);
  }

  if (column.accessorKey) {
    return row[column.accessorKey];
  }

  return "";
};

/**
 * GlassDataTable component
 * A comprehensive data table with glassmorphism styling
 */
export const GlassDataTable = <T extends GlassDataTableRow = GlassDataTableRow>(
  props: GlassDataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => {
  return <GlassDataTableInner {...props} />;
};

const GlassDataTableInnerBase = <
  T extends GlassDataTableRow = GlassDataTableRow,
>(
  {
    data: incomingData = [],
    columns: incomingColumns = [],
    loading = false,
    emptyMessage = "No data available",
    variant = "default",
    size = "md",
    sortable = true,
    filterable = false,
    searchable = true,
    searchPlaceholder = "Search...",
    pagination = true,
    pageSizeOptions: incomingPageSizeOptions = [10, 25, 50, 100],
    initialPageSize = 10,
    compact = false,
    contained = false,
    maxHeight,
    maxWidth,
    showHeader = true,
    showFooter = true,
    selectable = false,
    selectionMode = "multiple",
    selectedRows: incomingSelectedRows = [],
    onSelectionChange,
    onRowClick,
    getRowId = (_row, index = 0) => index.toString(),
    getRowProps,
    stickyHeader = false,
    actions,
    cellRenderers,
    emptyState,
    className,
    // Consciousness features
    predictive = false,
    preloadContent = false,
    eyeTracking = false,
    gazeResponsive = false,
    adaptive = false,
    biometricResponsive = false,
    spatialAudio = false,
    audioFeedback = false,
    trackAchievements = false,
    achievementId,
    usageContext = "main",
    "aria-label": ariaLabel,
    ...restProps
  }: GlassDataTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const data = Array.isArray(incomingData) ? incomingData : [];
  const columns = Array.isArray(incomingColumns) ? incomingColumns : [];
  const effectiveInitialPageSize = compact
    ? Math.min(initialPageSize, 4)
    : initialPageSize;
  const pageSizeOptions =
    Array.isArray(incomingPageSizeOptions) && incomingPageSizeOptions.length
      ? incomingPageSizeOptions
      : [effectiveInitialPageSize];
  const selectedRows = Array.isArray(incomingSelectedRows)
    ? incomingSelectedRows
    : [];
  const tableRef = useRef<HTMLDivElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const [columnUsage, setColumnUsage] = useState<Record<string, number>>({});
  const [predictedSortColumn, setPredictedSortColumn] = useState<string | null>(
    null
  );
  const [adaptivePageSize, setAdaptivePageSize] = useState(
    effectiveInitialPageSize
  );
  const [interactionHeatmap, setInteractionHeatmap] = useState<
    Record<string, number>
  >({});

  // Consciousness feature hooks - only initialize if features are enabled
  const predictiveEngine = predictive ? usePredictiveEngine() : null;
  const eyeTracker = eyeTracking ? useEyeTracking() : null;
  const biometricAdapter = adaptive ? useBiometricAdaptation() : null;
  const spatialAudioEngine = spatialAudio ? useSpatialAudio() : null;
  const achievementTracker = trackAchievements ? useAchievements() : null;
  const interactionRecorder =
    predictive || trackAchievements
      ? useInteractionRecorder(`glass-datatable-${usageContext}`)
      : null;
  const [sortState, setSortState] = useState<SortState | null>(null);
  const [filterState, setFilterState] = useState<FilterState>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(effectiveInitialPageSize);
  const activePageSize = Math.max(
    1,
    Number.isFinite(biometricResponsive ? adaptivePageSize : pageSize)
      ? biometricResponsive
        ? adaptivePageSize
        : pageSize
      : 10
  );

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      tableRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref]
  );

  // Ensure size is always valid
  const validSize = compact
    ? "sm"
    : size && ["sm", "md", "lg"].includes(size)
      ? size
      : "md";
  const resolvedMaxHeight =
    typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
  const resolvedMaxWidth =
    typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;

  // Biometric adaptation for table density and pagination
  useEffect(() => {
    if (!biometricResponsive || !biometricAdapter) return;

    const adaptTable = () => {
      const stressLevel = biometricAdapter.currentStressLevel;

      // Adapt page size based on stress level
      if (stressLevel > 0.7) {
        setAdaptivePageSize(Math.min(initialPageSize, 10)); // Smaller pages when stressed
      } else if (stressLevel < 0.3) {
        setAdaptivePageSize(Math.max(initialPageSize, 25)); // Larger pages when relaxed
      } else {
        setAdaptivePageSize(initialPageSize); // Use original page size
      }
    };

    // Initial adaptation
    adaptTable();

    // Listen for biometric changes
    const interval = setInterval(adaptTable, ANIMATION.DURATION.slower * 7);
    return () => clearInterval(interval);
  }, [biometricResponsive, biometricAdapter, initialPageSize]);

  // Predictive sorting based on column usage patterns
  useEffect(() => {
    if (!predictive || !predictiveEngine) return;

    const updatePredictions = () => {
      const predictions = predictiveEngine.predictions;
      const sortPrediction = predictions.find(
        (p) => p.type === "suggest" && p.metadata?.tableContext === usageContext
      );

      if (sortPrediction && sortPrediction.confidence > 0.8) {
        const columnId = sortPrediction.metadata.columnId;
        setPredictedSortColumn(typeof columnId === "string" ? columnId : null);
      } else {
        setPredictedSortColumn(null);
      }
    };

    const interval = setInterval(
      updatePredictions,
      ANIMATION.DURATION.slower * 4
    );
    updatePredictions(); // Initial update

    return () => clearInterval(interval);
  }, [predictive, predictiveEngine, usageContext]);

  // Eye tracking for data attention analysis
  useEffect(() => {
    if (!gazeResponsive || !eyeTracker || !tableRef.current) return;

    const handleCellGaze = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      const cellElement = target?.closest("td");
      if (!cellElement) return;
      const rowElement = cellElement.parentElement;
      const rowGroupElement = rowElement?.parentElement;
      if (!rowElement || !rowGroupElement) return;

      const rowIndex = Array.from(rowGroupElement.children).indexOf(rowElement);
      const cellIndex = Array.from(rowElement.children).indexOf(cellElement);
      const heatmapKey = `${rowIndex}-${cellIndex}`;

      setInteractionHeatmap((prev) => ({
        ...prev,
        [heatmapKey]: (prev[heatmapKey] || 0) + 1,
      }));

      if (spatialAudioEngine && audioFeedback) {
        spatialAudioEngine.playGlassSound("cell_attention", {
          x: cellElement.offsetLeft,
          y: cellElement.offsetTop,
          z: 0,
        });
      }
    };

    // Note: onGazeEnter/offGazeEnter not available on current eye tracker interface
    // eyeTracker.onGazeEnter?.(tableRef.current, handleCellGaze);

    return () => {
      // if (tableRef.current) {
      //   eyeTracker.offGazeEnter?.(tableRef.current, handleCellGaze);
      // }
    };
  }, [gazeResponsive, eyeTracker, spatialAudioEngine, audioFeedback]);

  const sizeClasses = {
    sm: "glass-text-xs",
    md: "glass-text-sm",
    lg: "glass-text-base",
  };

  const cellPaddingClasses = {
    sm: "glass-px-3 glass-py-2",
    md: "glass-px-4 glass-py-3",
    lg: "glass-px-6 glass-py-4",
  };

  // Filter and search data
  const filteredData = useMemo(() => {
    let result = [...(data || [])];

    // Apply search
    if (searchQuery) {
      result = result.filter((row) =>
        columns.some((column) => {
          const value = getColumnValue(row, column);
          return String(value)
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        })
      );
    }

    // Apply filters
    Object.entries(filterState).forEach(([columnId, filterValue]) => {
      if (filterValue) {
        const column = columns.find((col) => col.id === columnId);
        if (column) {
          result = result.filter((row) => {
            const value = getColumnValue(row, column);
            return String(value)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase());
          });
        }
      }
    });

    return result;
  }, [data, columns, searchQuery, filterState]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortState || !filteredData) return filteredData || [];

    const column = columns.find(
      (col) => (col.id || `col-${columns.indexOf(col)}`) === sortState.id
    );
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = getColumnValue(a, column);
      const bValue = getColumnValue(b, column);
      const aComparable =
        typeof aValue === "number" || typeof aValue === "string"
          ? aValue
          : String(aValue);
      const bComparable =
        typeof bValue === "number" || typeof bValue === "string"
          ? bValue
          : String(bValue);

      if (aComparable < bComparable) return sortState.desc ? 1 : -1;
      if (aComparable > bComparable) return sortState.desc ? -1 : 1;
      return 0;
    });
  }, [filteredData, sortState, columns]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination || !sortedData) return sortedData || [];

    const startIndex = (currentPage - 1) * activePageSize;
    return sortedData.slice(startIndex, startIndex + activePageSize);
  }, [sortedData, currentPage, activePageSize, pagination]);

  const totalPages = Math.max(
    1,
    Math.ceil(((sortedData || [])?.length || 0) / activePageSize)
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Enhanced sorting with consciousness tracking
  const handleSort = useCallback(
    (columnId: string) => {
      const column = columns.find(
        (col) => (col.id || `col-${columns.indexOf(col)}`) === columnId
      );
      if (!column?.sortable && !sortable) return;

      // Track column usage for predictive sorting
      if (predictive) {
        setColumnUsage((prev) => ({
          ...prev,
          [columnId]: (prev[columnId] || 0) + 1,
        }));
      }

      // Record interaction for learning
      if (interactionRecorder) {
        interactionRecorder.recordClick({
          target: { id: `column-sort-${columnId}` },
          currentTarget: document.createElement("div"),
          clientX: 0,
          clientY: 0,
          pageX: 0,
          pageY: 0,
          screenX: 0,
          screenY: 0,
          movementX: 0,
          movementY: 0,
          button: 0,
          buttons: 0,
          altKey: false,
          ctrlKey: false,
          metaKey: false,
          shiftKey: false,
          getModifierState: () => false,
          preventDefault: () => {},
          stopPropagation: () => {},
          isDefaultPrevented: () => false,
          isPropagationStopped: () => false,
          nativeEvent: new MouseEvent("click"),
          persist: () => {},
        } as unknown as React.MouseEvent<Element>);
      }

      // Track achievements
      if (achievementTracker && trackAchievements) {
        achievementTracker.recordAction("table_sort", {
          columnId,
          usage: (columnUsage[columnId] || 0) + 1,
          context: usageContext,
        });
      }

      // Play spatial audio feedback
      if (spatialAudioEngine && audioFeedback) {
        spatialAudioEngine.playGlassSound("sort_column");
      }

      setSortState((prev) => {
        if (prev?.id === columnId) {
          return prev.desc ? null : { id: columnId, desc: true };
        }
        return { id: columnId, desc: false };
      });
    },
    [
      columns,
      sortable,
      predictive,
      interactionRecorder,
      achievementTracker,
      trackAchievements,
      spatialAudioEngine,
      audioFeedback,
      columnUsage,
      usageContext,
    ]
  );

  // Handle selection
  const handleRowSelection = (rowId: string, selected: boolean) => {
    if (!onSelectionChange) return;

    if (selectionMode === "single") {
      onSelectionChange(selected ? [rowId] : []);
    } else {
      const newSelection = selected
        ? [...selectedRows, rowId]
        : selectedRows.filter((id) => id !== rowId);
      onSelectionChange(newSelection);
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (!onSelectionChange) return;

    if (selected) {
      const allIds = (paginatedData || []).map((row, index) =>
        getRowId(row, index)
      );
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const isAllSelected =
    (paginatedData || []).length > 0 &&
    (paginatedData || []).every((row, index) =>
      selectedRows.includes(getRowId(row, index))
    );

  return (
    <div
      data-glass-component
      ref={setContainerRef}
      className={cn("glass-w-full", className)}
      style={
        {
          color: TABLE_TEXT,
        } as React.CSSProperties
      }
      aria-label={ariaLabel}
      {...restProps}
    >
      {/* Table header with search and actions */}
      {showHeader && (searchable || actions || filterable) && (
        <div
          className={cn(
            "glass-flex glass-flex-wrap glass-items-center glass-justify-between glass-gap-3",
            compact ? "glass-mb-2" : "glass-mb-4"
          )}
        >
          <div className="glass-flex glass-min-w-0 glass-flex-1 glass-items-center glass-gap-3">
            {searchable && (
              <div
                className={cn(
                  "glass-flex glass-w-full glass-items-center glass-gap-2 glass-px-3",
                  compact ? "sm:glass-w-48" : "sm:glass-w-64"
                )}
                style={tableControlStyle}
              >
                <svg
                  aria-hidden="true"
                  className="glass-w-4 glass-h-4 glass-flex-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="search"
                  aria-label={searchPlaceholder}
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="glass-min-w-0 glass-flex-1 glass-bg-transparent glass-outline-none"
                  style={{
                    color: TABLE_TEXT,
                    width: "100%",
                    border: 0,
                    outline: 0,
                    background: "transparent",
                    appearance: "none",
                  }}
                />
              </div>
            )}
          </div>

          {actions && (
            <div className="glass-flex glass-flex-wrap glass-items-center glass-gap-2">
              {actions}
            </div>
          )}
        </div>
      )}

      {/* Table container */}
      <OptimizedGlass
        elevation={"level2"}
        intensity="medium"
        depth={2}
        tint="neutral"
        border="subtle"
        animation="none"
        performanceMode="medium"
        className={cn(
          "glass-overflow-hidden glass-contrast-guard",
          !prefersReducedMotion &&
            `transition-all duration-[${ANIMATION.DURATION.slow}ms]`,
          variant === "bordered" && "glass-border glass-border-glass-border/20"
        )}
        style={{
          ...createGlassStyle({ intent: "neutral", elevation: "level2" }),
          background:
            "linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.018))",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -1px 0 rgba(255,255,255,0.035), 0 20px 48px rgba(3,7,18,0.18)",
          borderColor: "rgba(255, 255, 255, 0.16)",
          color: TABLE_TEXT,
          maxHeight:
            resolvedMaxHeight ?? (compact || contained ? "240px" : undefined),
          maxWidth:
            resolvedMaxWidth ?? (compact || contained ? "320px" : undefined),
          overflow:
            compact || contained || resolvedMaxHeight ? "auto" : undefined,
        }}
      >
        {/* Mobile rows use a labelled, stacked layout so values remain readable
            without requiring precision horizontal scrolling. */}
        <div
          className="glass-flex glass-flex-col glass-gap-3 glass-p-3 sm:glass-hidden"
          data-testid="glass-data-table-mobile"
        >
          {loading ? (
            <div
              className="glass-flex glass-min-h-24 glass-items-center glass-justify-center glass-text-sm"
              role="status"
              style={tableSecondaryTextStyle}
            >
              Loading...
            </div>
          ) : paginatedData.length === 0 ? (
            <div
              className="glass-flex glass-min-h-24 glass-items-center glass-justify-center glass-text-sm"
              style={tableSecondaryTextStyle}
            >
              {emptyState?.message || emptyMessage}
            </div>
          ) : (
            paginatedData.map((row, index) => {
              const rowId = getRowId(row, index);
              const isSelected = selectedRows.includes(rowId);

              return (
                <div
                  key={`mobile-${rowId}`}
                  className={cn(
                    "glass-radius-xl glass-border glass-p-4",
                    onRowClick && "glass-cursor-pointer",
                    isSelected && "glass-ring-1 glass-ring-white/40"
                  )}
                  style={{
                    background:
                      "linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0.018))",
                    borderColor: "rgba(255, 255, 255, 0.14)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.14), 0 8px 20px rgba(3,7,18,0.12)",
                  }}
                  onClick={() => onRowClick?.(row)}
                >
                  <dl className="glass-flex glass-flex-col glass-gap-3">
                    {columns.map((column, colIndex) => {
                      const columnId = column.id || `col-${colIndex}`;
                      const value = getColumnValue(row, column);
                      const header =
                        typeof column.header === "function"
                          ? column.header({ column })
                          : column.header;
                      const rendererKey =
                        column.id || (column.accessorKey as string) || "";
                      const renderer = cellRenderers?.[rendererKey];

                      return (
                        <div
                          key={`mobile-${rowId}-${columnId}`}
                          className="glass-flex glass-min-w-0 glass-flex-col glass-gap-1"
                        >
                          <dt
                            className="glass-text-xs glass-font-semibold"
                            style={tableSecondaryTextStyle}
                          >
                            {header}:
                          </dt>
                          <dd
                            className="glass-min-w-0 glass-pl-2 glass-text-sm"
                            style={{
                              ...tableTextStyle,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {column.cell
                              ? column.cell({ row, value })
                              : renderer
                                ? renderer(value, row)
                                : String(value ?? "")}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              );
            })
          )}
        </div>

        <div className="glass-hidden glass-w-full glass-overflow-x-auto sm:glass-block">
          <table
            className="glass-w-full glass-table-fixed"
            style={{
              color: TABLE_TEXT,
              minWidth: compact
                ? "100%"
                : columns.length > 3
                  ? `${Math.max(640, columns.length * 152)}px`
                  : "100%",
            }}
          >
            {/* Table header */}
            <thead
              className={cn(
                "glass-relative bg-muted/20 glass-border-b glass-border-glass-border/20",
                stickyHeader && "sticky top-0 z-10"
              )}
              style={{ background: "rgba(255, 255, 255, 0.065)" }}
            >
              <tr>
                {selectable && (
                  <th
                    className={cn("glass-w-12", cellPaddingClasses[validSize])}
                  >
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      checked={isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ accentColor: "rgba(51, 65, 85, 0.92)" }}
                    />
                  </th>
                )}

                {(columns || []).map((column, index) => {
                  const columnId = column.id || `col-${index}`;
                  const canSort = column.sortable ?? sortable;
                  const headerContent =
                    typeof column.header === "function"
                      ? column.header({ column })
                      : column.header;

                  return (
                    <th
                      key={columnId}
                      scope="col"
                      aria-sort={
                        sortState?.id === columnId
                          ? sortState.desc
                            ? "descending"
                            : "ascending"
                          : "none"
                      }
                      className={cn(
                        "glass-font-semibold glass-text-primary glass-border-b glass-border-glass-border/10",
                        !prefersReducedMotion &&
                          `transition-all duration-[${ANIMATION.DURATION.fast}ms]`,
                        cellPaddingClasses[validSize],
                        sizeClasses[validSize],
                        {
                          "glass-text-center": column.align === "center",
                          "glass-text-right": column.align === "right",
                          "glass-cursor-pointer hover:bg-muted/10 hover:glass-text-primary":
                            canSort,
                        }
                      )}
                      style={{
                        width: column.width,
                        color: TABLE_TEXT,
                      }}
                      onClick={() => canSort && handleSort(columnId)}
                    >
                      <div className="glass-flex glass-items-center glass-gap-2 glass-min-w-0">
                        <ContrastGuard
                          autoAdjust={false}
                          style={tableTextStyle}
                        >
                          <span className="glass-min-w-0 glass-break-words">
                            {headerContent}
                          </span>
                        </ContrastGuard>

                        {canSort && (
                          <div className="glass-flex glass-flex-col">
                            <svg
                              className={cn(
                                "glass-w-3 glass-h-3 -glass-mb-1",
                                !prefersReducedMotion && "transition-colors",
                                sortState?.id === columnId && !sortState.desc
                                  ? "glass-text-primary"
                                  : "glass-text-secondary hover:glass-text-primary"
                              )}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                            </svg>
                            <svg
                              className={cn(
                                "glass-w-3 glass-h-3 rotate-180",
                                !prefersReducedMotion && "transition-colors",
                                sortState?.id === columnId && sortState.desc
                                  ? "glass-text-primary"
                                  : "glass-text-secondary hover:glass-text-primary"
                              )}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table body */}
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={columns.length + (selectable ? 1 : 0)}
                    className={cn(
                      "glass-text-center",
                      cellPaddingClasses[validSize]
                    )}
                  >
                    <div
                      className="glass-flex glass-items-center glass-justify-center glass-gap-2 glass-py-8"
                      role="status"
                      aria-live="polite"
                    >
                      <div
                        className={cn(
                          "glass-w-4 glass-h-4 glass-border-2 glass-border-primary glass-border-t-transparent glass-radius-full",
                          !prefersReducedMotion && "glass-animate-spin"
                        )}
                      />
                      <ContrastGuard
                        autoAdjust={false}
                        style={tableSecondaryTextStyle}
                      >
                        <span className="glass-text-secondary">Loading...</span>
                      </ContrastGuard>
                    </div>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (selectable ? 1 : 0)}
                    className={cn(
                      "glass-text-center glass-text-secondary py-8 glass-break-words",
                      cellPaddingClasses[validSize]
                    )}
                  >
                    {emptyState ? (
                      <div className="glass-flex glass-flex-col glass-items-center glass-gap-2">
                        {emptyState.icon}
                        <ContrastGuard
                          autoAdjust={false}
                          style={tableTextStyle}
                        >
                          <div className="glass-font-medium">
                            {emptyState.message || emptyMessage}
                          </div>
                        </ContrastGuard>
                        {emptyState.description && (
                          <ContrastGuard
                            autoAdjust={false}
                            style={tableSecondaryTextStyle}
                          >
                            <div className="glass-text-sm glass-text-secondary">
                              {emptyState.description}
                            </div>
                          </ContrastGuard>
                        )}
                      </div>
                    ) : (
                      <ContrastGuard
                        autoAdjust={false}
                        style={tableSecondaryTextStyle}
                      >
                        {emptyMessage}
                      </ContrastGuard>
                    )}
                  </td>
                </tr>
              ) : (
                (paginatedData || []).map((row, index) => {
                  const rowId = getRowId(row, index);
                  const isSelected = selectedRows.includes(rowId);
                  const rowProps = getRowProps?.(row) || {};

                  return (
                    <tr
                      key={rowId}
                      className={cn(
                        "group glass-radius-md",
                        !prefersReducedMotion &&
                          `transition-all duration-[${ANIMATION.DURATION.fast}ms]`,
                        {
                          "bg-muted/5":
                            variant === "striped" && index % 2 === 1,
                          "glass-shadow-md glass-ring-1 glass-ring-white/30":
                            isSelected,
                          "hover:bg-muted/10 glass-cursor-pointer hover:shadow-lg hover:shadow-primary/10 glass-hover--translate-y-0-5 hover:ring-1 hover:ring-white/10":
                            onRowClick && !prefersReducedMotion,
                          "hover:bg-muted/10 glass-cursor-pointer hover:ring-1 hover:ring-white/10":
                            onRowClick && prefersReducedMotion,
                          // Consciousness feature styles
                          "glass-ring-1 glass-ring-white/30":
                            gazeResponsive &&
                            interactionHeatmap[`${index}-0`] > 5,
                          "animate-pulse":
                            predictedSortColumn &&
                            columns.some(
                              (col) =>
                                (col.id || `col-${columns.indexOf(col)}`) ===
                                predictedSortColumn
                            ),
                          "transform-gpu":
                            gazeResponsive || biometricResponsive,
                        }
                      )}
                      onClick={(e) => onRowClick?.(row)}
                      {...rowProps}
                    >
                      {selectable && (
                        <td className={cellPaddingClasses[validSize]}>
                          <input
                            type="checkbox"
                            aria-label={`Select row ${index + 1}`}
                            checked={isSelected}
                            onChange={(e) =>
                              handleRowSelection(rowId, e.target.checked)
                            }
                            style={{ accentColor: "rgba(51, 65, 85, 0.92)" }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      )}

                      {(columns || []).map((column, colIndex) => {
                        const columnId = column.id || `col-${colIndex}`;
                        const value = getColumnValue(row, column);

                        return (
                          <td
                            key={columnId}
                            className={cn(
                              "glass-border-b glass-border-glass-border/5 glass-text-primary/80 glass-align-top glass-break-words group-hover:glass-text-primary",
                              !prefersReducedMotion && "transition-colors",
                              cellPaddingClasses[validSize],
                              sizeClasses[validSize],
                              {
                                "glass-text-center": column.align === "center",
                                "glass-text-right": column.align === "right",
                              }
                            )}
                            style={{
                              color: TABLE_TEXT,
                            }}
                          >
                            <div className="glass-min-w-0 glass-max-w-full glass-break-words">
                              <ContrastGuard
                                autoAdjust={false}
                                style={tableTextStyle}
                              >
                                {column.cell
                                  ? column.cell({ row, value })
                                  : (() => {
                                      const rendererKey =
                                        column.id ||
                                        (column.accessorKey as string) ||
                                        "";
                                      const renderer =
                                        cellRenderers?.[rendererKey];
                                      return renderer
                                        ? renderer(value, row)
                                        : String(value ?? "");
                                    })()}
                              </ContrastGuard>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {showFooter &&
          pagination &&
          !loading &&
          (paginatedData || []).length > 0 && (
            <div
              className={cn(
                "glass-flex glass-flex-wrap glass-items-center glass-justify-between glass-gap-3 glass-border-t glass-border-glass-border/10 glass-surface-subtle",
                compact ? "glass-px-2 glass-py-2" : "glass-px-4 glass-py-3"
              )}
            >
              <div className="glass-flex glass-items-center glass-gap-2">
                <span
                  className="glass-text-sm glass-text-secondary"
                  role="status"
                >
                  Showing {(currentPage - 1) * activePageSize + 1} to{" "}
                  {Math.min(
                    currentPage * activePageSize,
                    (sortedData || []).length
                  )}{" "}
                  of {(sortedData || []).length} results
                </span>
              </div>

              <div className="glass-flex glass-flex-wrap glass-items-center glass-gap-4">
                <div className="glass-flex glass-items-center glass-gap-2">
                  <ContrastGuard
                    autoAdjust={false}
                    style={tableSecondaryTextStyle}
                  >
                    <span className="glass-text-sm glass-text-secondary">
                      Rows per page:
                    </span>
                  </ContrastGuard>
                  <select
                    className="glass-control glass-frost-control"
                    aria-label="Rows per page"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{
                      ...tableNativeControlStyle,
                      minHeight: 36,
                      padding: "0 30px 0 12px",
                    }}
                  >
                    {(pageSizeOptions || []).map((optionSize) => (
                      <option key={optionSize} value={optionSize}>
                        {optionSize}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="glass-flex glass-items-center glass-gap-1">
                  <button
                    className="glass-control glass-frost-control"
                    type="button"
                    disabled={currentPage === 1}
                    onClick={(e) => setCurrentPage(1)}
                    aria-label="First page"
                    style={{
                      ...tableNativeControlStyle,
                      minHeight: 36,
                      minWidth: 36,
                    }}
                  >
                    ←
                  </button>
                  <button
                    className="glass-control glass-frost-control"
                    type="button"
                    disabled={currentPage === 1}
                    onClick={(e) => setCurrentPage((p) => p - 1)}
                    aria-label="Previous page"
                    style={{
                      ...tableNativeControlStyle,
                      minHeight: 36,
                      minWidth: 36,
                    }}
                  >
                    ‹
                  </button>

                  <ContrastGuard autoAdjust={false} style={tableTextStyle}>
                    <span className="glass-px-3 glass-py-1 glass-text-sm">
                      {currentPage} of {totalPages}
                    </span>
                  </ContrastGuard>

                  <button
                    className="glass-control glass-frost-control"
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={(e) => setCurrentPage((p) => p + 1)}
                    aria-label="Next page"
                    style={{
                      ...tableNativeControlStyle,
                      minHeight: 36,
                      minWidth: 36,
                    }}
                  >
                    ›
                  </button>
                  <button
                    className="glass-control glass-frost-control"
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={(e) => setCurrentPage(totalPages)}
                    aria-label="Last page"
                    style={{
                      ...tableNativeControlStyle,
                      minHeight: 36,
                      minWidth: 36,
                    }}
                  >
                    →
                  </button>
                </div>
              </div>
            </div>
          )}
      </OptimizedGlass>
    </div>
  );
};

const GlassDataTableInner = forwardRef(GlassDataTableInnerBase) as (<
  T extends GlassDataTableRow = GlassDataTableRow,
>(
  props: GlassDataTableProps<T> & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null) & { displayName?: string };

GlassDataTableInner.displayName = "GlassDataTable";

/**
 * Enhanced GlassDataTable with consciousness features enabled by default
 * Use this for tables that should be intelligent and adaptive
 */
export const ConsciousGlassDataTable = <
  T extends GlassDataTableRow = GlassDataTableRow,
>(
  props: GlassDataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => (
  <GlassDataTable
    predictive={true}
    adaptive={true}
    biometricResponsive={true}
    trackAchievements={true}
    achievementId="conscious_table_usage"
    usageContext="list"
    {...props}
  />
);

/**
 * Predictive data table that learns user sorting and filtering patterns
 */
export const PredictiveDataTable = <
  T extends GlassDataTableRow = GlassDataTableRow,
>(
  props: GlassDataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => (
  <GlassDataTable
    predictive={true}
    preloadContent={true}
    trackAchievements={true}
    achievementId="predictive_table_usage"
    usageContext="list"
    {...props}
  />
);

/**
 * Gaze-responsive data table with eye tracking for enhanced data exploration
 */
export const GazeResponsiveDataTable = <
  T extends GlassDataTableRow = GlassDataTableRow,
>(
  props: GlassDataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => (
  <GlassDataTable
    eyeTracking={true}
    gazeResponsive={true}
    spatialAudio={true}
    audioFeedback={true}
    trackAchievements={true}
    achievementId="gaze_table_interaction"
    usageContext="list"
    {...props}
  />
);

/**
 * Accessibility-focused data table with biometric adaptation and spatial audio
 */
export const AccessibleDataTable = <
  T extends GlassDataTableRow = GlassDataTableRow,
>(
  props: GlassDataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => (
  <GlassDataTable
    adaptive={true}
    biometricResponsive={true}
    spatialAudio={true}
    audioFeedback={true}
    trackAchievements={true}
    achievementId="accessible_table_usage"
    usageContext="list"
    {...props}
  />
);

/**
 * Pre-configured consciousness data table presets
 */
export const DataTableConsciousnessPresets = {
  /**
   * Minimal consciousness features for performance-sensitive contexts
   */
  minimal: {
    predictive: true,
    trackAchievements: true,
  },

  /**
   * Balanced consciousness features for general use
   */
  balanced: {
    predictive: true,
    adaptive: true,
    biometricResponsive: true,
    trackAchievements: true,
  },

  /**
   * Full consciousness features for immersive data exploration
   */
  immersive: {
    predictive: true,
    preloadContent: true,
    eyeTracking: true,
    gazeResponsive: true,
    adaptive: true,
    biometricResponsive: true,
    spatialAudio: true,
    audioFeedback: true,
    trackAchievements: true,
  },

  /**
   * Accessibility-focused consciousness features
   */
  accessible: {
    adaptive: true,
    biometricResponsive: true,
    spatialAudio: true,
    audioFeedback: true,
    trackAchievements: true,
  },
} as const;
